import reservedText from "../../../../../crates/baton-domain/src/workspace/reserved-addresses.txt?raw";

/**
 * A Team's web address, the slug of `/{ws}/…` (001 FR-156, the owner's decisions of 2026-09-29),
 * mirrored from the server so a form says what is wrong before it asks: 3 to 40 lower-case
 * letters, digits and single dashes, with no dash at either end, and none of the reserved words.
 * The reserved words are the server's own file, read here as text, so the two lists are one; the
 * server stays the authority and a form still sends what passes here.
 */
export const ADDRESS_MIN = 3;
export const ADDRESS_MAX = 40;

/** The reserved words: every top-level route and public path a Team address would shadow. */
export const RESERVED_ADDRESSES: ReadonlySet<string> = new Set(reservedText.split("\n").filter((line) => line.length > 0));

/** What is wrong with an address, in the order the server checks it, or null for a good one. */
export type AddressProblem = "length" | "characters" | "edge" | "double_dash" | "reserved";

export function addressProblem(slug: string): AddressProblem | null {
  if (slug.length < ADDRESS_MIN || slug.length > ADDRESS_MAX) return "length";
  if (!/^[a-z0-9-]+$/.test(slug)) return "characters";
  if (slug.startsWith("-") || slug.endsWith("-")) return "edge";
  if (slug.includes("--")) return "double_dash";
  if (RESERVED_ADDRESSES.has(slug)) return "reserved";
  return null;
}

/** Strip diacritics so "Émilie" derives "emilie" rather than an empty address. */
function ascii(value: string): string {
  return value.normalize("NFKD").replace(/[̀-ͯ]/g, "");
}

/** The address a Team's name suggests: its letters and digits in lower case, runs of anything else as one dash, cut to the maximum. */
export function addressFromName(name: string): string {
  return ascii(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, ADDRESS_MAX)
    .replace(/-+$/g, "");
}

/** An address as a person types it: lower case, with the spaces a name would have turned into dashes. */
export function typedAddress(value: string): string {
  return value.toLowerCase().replace(/\s+/g, "-");
}

/** `route` under the Team's new address when it is under `from`, else `route` unchanged. */
export function movedRoute(route: string, from: string, to: string): string {
  const prefix = `/${from}`;
  if (route === prefix) return `/${to}`;
  const next = route.charAt(prefix.length);
  if (route.startsWith(prefix) && (next === "/" || next === "?" || next === "#")) return `/${to}${route.slice(prefix.length)}`;
  return route;
}
